import { useMemo, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Button, Card, EmptyState, Pill, Skeleton, Suma } from '@/ui/primitives'
import { citesteMod, CursSelector } from '@/ui/curs-selector'
import {
  cursDeReferinta, eDatorie, lei, perecheDinLei, sursaCurs,
  type ContextCurs, type ModCurs,
} from '@/lib/money'

type Factura = {
  id: number; number: string; partner: string | null
  total_value: number; currency: string; exchange_rate: number | null; invoice_type: string
  restant_ron: number; due_date: string | null; booking_number: string | null
}
type PaginaFacturi = { rows: Factura[]; total: number; eur_rate: number }
/** Atentie: endpointul NU intoarce o lista, ci un obiect cu cheia `payments`
 *  (verificat pe raspunsul real). Tipat gresit, ecranul afisa lista goala, fara eroare. */
type ListaIncasari = { payments: Incasare[]; current_eur_rate: number }
type Incasare = {
  id: number; amount: number; currency: string; exchange_rate: number; amount_ron: number
  payment_date: string | null; payment_method: string | null; partner_name: string | null
  reference_number: string | null; invoice_id: number | null
}

const METODE = ['Transfer bancar', 'Ordin de plata', 'Numerar', 'Card', 'Dispozitie de plata', 'Tichete vacanta']
const azi = () => new Date().toISOString().slice(0, 10)
const zi = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' }) : '—'

export type Directie = 'income' | 'expense'

const TEXTE = {
  income: {
    titlu: 'Incasari', tipFactura: 'Sales', verb: 'Incaseaza', actiune: 'Inregistreaza incasarea',
    listaGol: 'Nimic de incasat', totalEticheta: 'total de incasat', restEticheta: 'rest de incasat',
    coloanaPartener: 'Client', recente: 'Ultimele incasari', nimicRecent: 'Nicio incasare inregistrata inca',
    tot: '{T.tot}', dupa: 'rest dupa incasare',
  },
  expense: {
    titlu: 'Plati', tipFactura: 'Purchase', verb: 'Plateste', actiune: 'Inregistreaza plata',
    listaGol: 'Nimic de platit', totalEticheta: 'total de platit', restEticheta: 'rest de platit',
    coloanaPartener: 'Furnizor', recente: 'Ultimele plati', nimicRecent: 'Nicio plata inregistrata inca',
    tot: 'plateste tot restul', dupa: 'rest dupa plata',
  },
} as const

export function FluxBani({ directie }: { directie: Directie }) {
  const T = TEXTE[directie]
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [mod, setMod] = useState<ModCurs>(citesteMod)
  const [manual, setManual] = useState<number | null>(null)
  const [sel, setSel] = useState<Factura | null>(null)

  const [suma, setSuma] = useState('')
  const [moneda, setMoneda] = useState<'RON' | 'EUR'>('RON')
  const [data, setData] = useState(azi)
  const [metoda, setMetoda] = useState(METODE[0]!)
  const [referinta, setReferinta] = useState('')

  const facturi = useQuery({
    queryKey: ['de-platit', directie, q],
    queryFn: () => api.get<PaginaFacturi>('/invoices/page',
      { invoice_type: T.tipFactura, status: 'open', q, per_page: 60, sort: 'due_date', sort_dir: 'asc' }),
    placeholderData: keepPreviousData,
  })
  const recente = useQuery({
    queryKey: ['plati-recente', directie],
    queryFn: () => api.get<ListaIncasari>('/payments/', { type: directie, limit: 15 }),
  })

  const ctx: ContextCurs = { bnrPlus2: facturi.data?.eur_rate ?? 0, mod, manual }
  const rows = (facturi.data?.rows ?? []).filter((r) => eDatorie(r.restant_ron))

  const calcul = useMemo(() => {
    if (!sel) return null
    const curs = cursDeReferinta(sel, ctx)
    const val = Number(String(suma).replace(',', '.')) || 0
    const inLei = moneda === 'RON' ? val : val * curs
    const restDupa = sel.restant_ron - inLei
    return { curs, inLei, restDupa, acopera: Math.abs(restDupa) < 0.01 || !eDatorie(Math.max(0, restDupa)) }
  }, [sel, suma, moneda, ctx])

  const inregistreaza = useMutation({
    mutationFn: () => {
      if (!sel || !calcul) throw new Error('Alege o factura')
      return api.post('/payments/', {
        type: directie,
        invoice_id: sel.id,
        amount: Number(String(suma).replace(',', '.')),
        currency: moneda,
        exchange_rate: moneda === 'EUR' ? calcul.curs : undefined,
        payment_date: data,
        payment_method: metoda,
        reference_number: referinta || undefined,
        partner_name: sel.partner ?? undefined,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['de-platit'] })
      qc.invalidateQueries({ queryKey: ['plati-recente'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      setSuma(''); setReferinta(''); setSel(null)
    },
  })

  const camp = 'h-9 w-full px-3 rounded-[var(--radius-control)] border border-line bg-surface text-[14px]'
  const eticheta = 'text-[12.5px] text-muted mb-1 block'

  return (
    <>
      <PageHeader
        title={T.titlu}
        count={facturi.data ? `${rows.length} facturi` : undefined}
        actions={<CursSelector ctx={ctx} onMod={setMod} onManual={setManual} />}
      />

      <div className="px-8 py-6 grid gap-6 lg:grid-cols-[1fr_400px] items-start">
        <div className="space-y-5 min-w-0">
          <div className="flex items-center gap-3">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cauta factura, partener, comanda…"
              className={camp + ' max-w-sm'}
            />
            {facturi.data && (
              <span className="text-[13px] text-muted tnum">
                {T.totalEticheta} {lei(rows.reduce((s, r) => s + r.restant_ron, 0))} lei
              </span>
            )}
          </div>

          <Card className="overflow-hidden">
            {facturi.isPending ? <Skeleton /> : rows.length === 0 ? (
              <EmptyState title={T.listaGol} hint="Toate facturile din filtru sunt achitate." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="text-left text-[11.5px] uppercase tracking-[0.1em] text-muted">
                      <th className="font-medium px-5 py-3">Factura</th>
                      <th className="font-medium px-5 py-3">{T.coloanaPartener}</th>
                      <th className="font-medium px-5 py-3">Scadenta</th>
                      <th className="font-medium px-5 py-3 text-right">Rest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const activ = sel?.id === r.id
                      const zileIntarziere = r.due_date
                        ? Math.round((Date.now() - new Date(r.due_date).getTime()) / 86_400_000) : 0
                      return (
                        <tr
                          key={r.id}
                          onClick={() => { setSel(r); setMoneda(r.currency === 'EUR' ? 'EUR' : 'RON') }}
                          className={'border-t border-line cursor-pointer ' +
                            (activ ? 'bg-accent-soft' : 'hover:bg-page/70')}
                        >
                          <td className="px-5 py-3">
                            <span className="tnum font-medium">{r.number}</span>
                            {r.booking_number && <span className="ml-2 text-[12px] text-muted">rez. {r.booking_number}</span>}
                          </td>
                          <td className="px-5 py-3 max-w-[240px] truncate">{r.partner ?? '—'}</td>
                          <td className="px-5 py-3">
                            {zileIntarziere > 0
                              ? <Badge tone="bad">restant {zileIntarziere} z</Badge>
                              : <span className="text-muted tnum text-[13px]">{zi(r.due_date)}</span>}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <Suma {...perecheDinLei(r.restant_ron, r, ctx)} explicatie={sursaCurs(r, ctx)} />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <div>
            <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted mb-2">{T.recente}</h2>
            <Card className="overflow-hidden">
              {recente.isPending ? <Skeleton rows={4} /> : (recente.data?.payments ?? []).length === 0 ? (
                <EmptyState title={T.nimicRecent} />
              ) : (
                <table className="w-full text-[14px]">
                  <tbody>
                    {(recente.data?.payments ?? []).map((p) => (
                      <tr key={p.id} className="border-t border-line first:border-0">
                        <td className="px-5 py-2.5 text-muted tnum text-[13px] w-24">{zi(p.payment_date)}</td>
                        <td className="px-5 py-2.5 max-w-[220px] truncate">{p.partner_name ?? '—'}</td>
                        <td className="px-5 py-2.5 text-[13px] text-muted">{p.payment_method ?? ''}</td>
                        <td className="px-5 py-2.5 text-right tnum">
                          {lei(p.amount)} <span className="text-[11.5px] text-muted">{p.currency}</span>
                          {p.currency !== 'RON' && (
                            <span className="ml-2 text-[12px] text-muted">la {p.exchange_rate?.toFixed(4)}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        </div>

        <Card className="p-5 lg:sticky lg:top-6">
          {!sel ? (
            <EmptyState title="Alege o factura" hint="Din lista din stanga." />
          ) : (
            <form
              className="space-y-4"
              onSubmit={(e) => { e.preventDefault(); inregistreaza.mutate() }}
            >
              <div>
                <p className="text-[12.5px] text-muted">{T.titlu.slice(0, -1)} pentru</p>
                <p className="font-semibold tnum">{sel.number}</p>
                <p className="text-[14px] text-muted truncate">{sel.partner}</p>
                <div className="mt-3 flex items-baseline justify-between">
                  <span className="text-[13px] text-muted">{T.restEticheta}</span>
                  <Suma {...perecheDinLei(sel.restant_ron, sel, ctx)} explicatie={sursaCurs(sel, ctx)} ton="strong" />
                </div>
              </div>

              <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <label className={eticheta} htmlFor="suma">Suma</label>
                  <input id="suma" value={suma} onChange={(e) => setSuma(e.target.value)}
                    inputMode="decimal" placeholder="0,00" required className={camp + ' tnum'} />
                </div>
                <div className="flex gap-1 pb-0.5">
                  {(['RON', 'EUR'] as const).map((m) => (
                    <Pill key={m} active={moneda === m} onClick={() => setMoneda(m)}>{m}</Pill>
                  ))}
                </div>
              </div>

              <button type="button"
                onClick={() => {
                  const curs = cursDeReferinta(sel, ctx)
                  setSuma((moneda === 'RON' ? sel.restant_ron : sel.restant_ron / curs).toFixed(2))
                }}
                className="text-[13px] text-accent hover:underline">
                {T.tot}
              </button>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={eticheta} htmlFor="data">Data</label>
                  <input id="data" type="date" value={data} onChange={(e) => setData(e.target.value)} className={camp} />
                </div>
                <div>
                  <label className={eticheta} htmlFor="metoda">Metoda</label>
                  <select id="metoda" value={metoda} onChange={(e) => setMetoda(e.target.value)} className={camp}>
                    {METODE.map((m) => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={eticheta} htmlFor="ref">Referinta (OP, chitanta)</label>
                <input id="ref" value={referinta} onChange={(e) => setReferinta(e.target.value)} className={camp} />
              </div>

              {calcul && Number(suma) > 0 && (
                <div className="rounded-[var(--radius-control)] bg-page p-3 space-y-1.5 text-[13.5px]">
                  <div className="flex justify-between">
                    <span className="text-muted">se inregistreaza</span>
                    <span className="tnum">{lei(calcul.inLei)} lei
                      {moneda === 'EUR' && <span className="text-muted"> · curs {calcul.curs.toFixed(4)}</span>}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted">{T.dupa}</span>
                    <span className={'tnum ' + (calcul.acopera ? 'text-good' : '')}>
                      {calcul.acopera ? 'stinsa integral' : `${lei(Math.max(0, calcul.restDupa))} lei`}
                    </span>
                  </div>
                  {calcul.restDupa < -0.01 && (
                    <p className="text-bad text-[12.5px]">
                      Suma depaseste restul cu {lei(Math.abs(calcul.restDupa))} lei.
                    </p>
                  )}
                </div>
              )}

              {inregistreaza.isError && (
                <p className="text-bad text-[13px]">{(inregistreaza.error as Error).message}</p>
              )}

              <div className="flex gap-2 pt-1">
                <Button type="submit" variant="primary" disabled={inregistreaza.isPending || !Number(suma)}>
                  {inregistreaza.isPending ? 'Se inregistreaza…' : T.actiune}
                </Button>
                <Button variant="quiet" onClick={() => setSel(null)}>Renunta</Button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </>
  )
}
